import React from 'react'
import {
  Button,
  ColorSquare,
  Icon,
  Modal,
  ModalProps,
  toastManager,
  Tooltip,
} from '@therms/atalaya'
import {
  Alerts,
  EventPresentation,
  type DownloadPdfResult,
  IdLabel,
  useDownloadsApiServiceContext,
  useEvent,
} from '@therms/react-modules'
import {downloadBase64AsPdf} from '@therms/web-js'
import dayjs from 'dayjs'
import {IoMdDownload} from 'react-icons/io'
import {MdRefresh} from 'react-icons/md'

interface EventModalProps extends Omit<ModalProps, 'children'> {
  eventId: string
}

function EventModal({eventId, ...modalProps}: EventModalProps) {
  const {data} = useEvent({eventId})
  const {client} = useDownloadsApiServiceContext()

  const handleDownloadEventPDF = () => {
    if (!data) return

    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    client.events.DownloadEvent({body: {eventId}})
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `${dayjs(data.event.date).format()} ${data.event.title} ${
              data.event.subTitle
            }`,
            result.pdfBase64,
          )
        }
      })
      .catch((err) => {
        console.error('err downloading report', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  return (
    <Modal
      {...modalProps}
      escPressClose={modalProps.escPressClose ?? true}
      header={
        <div className="flex items-start justify-between sm:items-end">
          <div className="space-x-xs flex flex-wrap items-baseline">
            <ColorSquare color={data?.eventType.color} />
            <span className="text-lg">{data?.eventType.name}</span>{' '}
            <IdLabel
              className="text-color-subtle text-base font-bold tracking-wide"
              idLabel={data?.event.idLabel}
            />
          </div>

          {!!data && (
            <Tooltip tooltip="Download PDF" className="pt-xxs flex items-end">
              <Button onClick={handleDownloadEventPDF} subtle size="xs">
                <Icon Icon={IoMdDownload} />
              </Button>
            </Tooltip>
          )}
        </div>
      }
      outsideClickClose={modalProps.outsideClickClose ?? true}
    >
      <EventPresentation eventId={eventId} hideEventName />
    </Modal>
  )
}

export {EventModal}
